import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronDown } from 'lucide-react';
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { fetchUserById, updateUser, resetPassword } from "../../services/apiService";
import { decodedToken, setAuthToken } from '../../utils/auth';
import permissionsConfig from '../../utils/permission.json';

const AccountInformation = () => {
  const decode = decodedToken();
  const navigate = useNavigate();
  const { id } = useParams();
  const [showDropdown, setShowDropdown] = useState(false);
  const [user, setUser] = useState(null);
  const [initialUser, setInitialUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  const isAdmin = decode?.userType === "powerAdmin";
  const Email = decode?.email;
  const isOwnAccount = user?.email === Email;

  const userTypeMapping = {
    "Power Admin": "powerAdmin",
    "Admin": "admin", 
    "OBM Expert": "obmExpert",
    "OBM RD": "obmRD",
    "OBM QM": "obmQM",
    "GPM": "gpm",
    "ODM": "odm"
  };

  const displayNameMapping = {
    "powerAdmin": "Power Admin",
    "admin": "Admin",
    "obmExpert": "OBM Expert",
    "obmRD": "OBM RD",
    "obmQM": "OBM QM",
    "gpm": "GPM",
    "odm": "ODM"
  };

  const PermissionMapper = {
    AiOpticalDetectionSystemAiOds: "Ai Optical Detection System Ai Ods",
    LLMuraSpotLineBandIssueLabeling: "LL Mura Spot Line Band Issue Labeling",
    MuraSpotLineBandIssueDetection: "Mura Spot Line Band Issue Detection",
    AudioIssueVisualization: "Audio Issue Visualization",
    StatisticsAndAnalysis: "Statistics and Analysis",
    HWChangeVerificationAssessment: "HW Change Verification Assessment",
    SWChangeVerificationAssessment: "SW Change Verification Assessment",
    ProjectReportingHub: "Project Reporting Hub",
    Certification: "Certification",
    VendorCapability: "Vendor Capability",
    Management: "Management",
    templateManagement: "Template Management",
    photoManagement: "Photo Management",
    audiobuzzManagement:"Audio Buzz Data Management"
  };

  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        const userData = await fetchUserById(id);
        setUser(userData);
        setInitialUser(JSON.parse(JSON.stringify(userData)));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadUserDetails();
  }, [id]);

  
  const getPermissionsForUserType = (userTypeEnum) => {
    if (!userTypeEnum) return {};

    const permissions = {};
    Object.keys(permissionsConfig).forEach(permission => {
      if (permissionsConfig[permission].hasOwnProperty(userTypeEnum)) {
        permissions[permission] = permissionsConfig[permission][userTypeEnum] || false;
      }
    });
    return permissions;
  };

  const getAvailablePermissions = () => {
    if (!user?.userType) return [];

    return Object.keys(permissionsConfig).filter(permission => 
      permissionsConfig[permission].hasOwnProperty(user.userType)
    );
  };

  const selectUserType = (displayName) => {
    if (isAdmin) {
      
      const backendEnumValue = userTypeMapping[displayName];
      const newPermissions = getPermissionsForUserType(backendEnumValue);
      
      setUser((prev) => ({ 
        ...prev, 
        userType: backendEnumValue, 
        permissions: newPermissions 
      }));
      setShowDropdown(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleAccountStatus = () => {
    if (isAdmin) {
      setUser((prev) => ({ ...prev, isAccountActive: !prev.isAccountActive }));
    }
  };

  const handlePermissionChange = (permissionKey, checked) => {
    if (isAdmin) {
      setUser((prev) => ({
        ...prev,
        permissions: { ...prev.permissions, [permissionKey]: checked },
      }));
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const response = await updateUser(user.email, {
        userId: user.userId,
        email: user.email,
        companyInfo: user.companyInfo,
        userType: user.userType,
        isAccountActive: user.isAccountActive,
        permissions: user.permissions,
      });
      const newUserType = user.userType;
      setInitialUser(JSON.parse(JSON.stringify(user)));
      
      if (user.email === Email) { 
        setAuthToken(response.token);
        if (newUserType !== "powerAdmin") {
          setAuthToken(response.token);
          navigate('/dashboard');
        } else {
          navigate('/account-management');
        }
      } else {
        navigate('/account-management');
      }
    } catch (err) {
      alert("Update failed: " + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) {
      Swal.fire("Error", "User email is missing!", "error");
      return;
    }
    try {
      const response = await resetPassword(user.email);
      Swal.fire("Success", response.message || "Password reset email sent!", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Password reset failed", "error");
    }
  };

  const hasChanges = () => {
    if (!user || !initialUser) return false;
    return (
      user.userId !== initialUser.userId ||
      user.companyInfo !== initialUser.companyInfo ||
      user.isAccountActive !== initialUser.isAccountActive ||
      user.userType !== initialUser.userType ||
      JSON.stringify(user.permissions) !== JSON.stringify(initialUser.permissions)
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-4">
        <div className="flex items-center mb-8">
          <div className="p-2 -ml-2">
            <div className="h-6 w-6 bg-gray-700 rounded animate-pulse" />
          </div>
          <div className="h-6 w-48 bg-gray-700 rounded animate-pulse ml-2" />
        </div>

        <div className="space-y-6">
          <div>
            <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-10 w-full bg-gray-700 rounded-lg animate-pulse" />
          </div>

          
          <div>
            <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-6 w-64 bg-gray-700 rounded animate-pulse" />
          </div>

          <div>
            <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-10 w-full bg-gray-700 rounded-lg animate-pulse" />
          </div>

          <div>
            <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-10 w-full bg-gray-700 rounded-lg animate-pulse flex justify-between items-center px-3">
              <div className="h-4 w-32 bg-gray-600 rounded animate-pulse" />
              <div className="h-5 w-5 bg-gray-600 rounded animate-pulse" />
            </div>
          </div>

          <div>
            <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-10 w-full bg-gray-700 rounded-lg animate-pulse" />
          </div>

          <div>
            <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-4" />
            <div className="space-y-3">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-800 rounded-lg">
                  <div className="h-4 w-48 bg-gray-700 rounded animate-pulse" />
                  <div className="flex items-center">
                    <div className="h-4 w-16 bg-gray-700 rounded animate-pulse mr-3" />
                    <div className="h-6 w-11 bg-gray-700 rounded-full animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="h-12 w-full bg-gray-700 rounded-lg animate-pulse" />
            <div className="h-12 w-full bg-gray-700 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error) return <div className="text-red-500">Error: {error}</div>;
  if (!user) return <div className="text-white">User not found</div>;

  const availablePermissions = getAvailablePermissions();

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <div className="flex items-center mb-8">
        <button className="p-2 -ml-2" onClick={() => navigate("/account-management")}>
          <ChevronLeft className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold ml-2">Account Information</h1>
      </div>

      <div className="space-y-6">
        <div>
          <div className="text-gray-400 text-sm">User Name</div>
          <input
            type="text"
            name="userId"
            value={user.userId}
            onChange={handleInputChange}
            className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none"
          />
        </div>

        <div>
          <div className="text-gray-400 text-sm">Email Address</div>
          <div className="text-white">{user.email}</div>
        </div>

        <div>
          <div className="text-gray-400 text-sm">Company Info</div>
          <input
            type="text"
            name="companyInfo"
            value={user.companyInfo}
            onChange={handleInputChange}
            maxLength="16"
            className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none"
          />
        </div>

        <div className="relative">
          <div className="text-gray-400 text-sm mb-2">User Type</div>
          <button
            type="button"
            onClick={() => isAdmin && setShowDropdown(!showDropdown)}
            className={`w-full bg-gray-700 text-white px-3 py-2 rounded-lg flex justify-between items-center ${
              !isAdmin ? "cursor-not-allowed opacity-50" : ""
            }`}
            disabled={!isAdmin}
          >

            <span>{user.userType ? displayNameMapping[user.userType] || user.userType : "Select User Type"}</span>
            <ChevronDown className="h-5 w-5" />
          </button>
          {showDropdown && isAdmin && (
            <div className="absolute left-0 right-0 mt-1 bg-gray-700 border border-gray-600 rounded-lg overflow-hidden z-10">
              {Object.keys(userTypeMapping).map((displayName) => (
                <button
                  key={displayName}
                  type="button"
                  onClick={() => selectUserType(displayName)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-600 text-white"
                >
                  {displayName}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-gray-400 text-sm mb-2">Account Status</div>
          <select
            name="isAccountActive"
            value={user.isAccountActive}
            onChange={(e) => isAdmin && !isOwnAccount && setUser((prev) => ({ ...prev, isAccountActive: e.target.value }))}
            className={`bg-gray-700 text-white px-3 py-2 rounded-lg w-full ${
              !isAdmin || isOwnAccount ? "cursor-not-allowed opacity-50" : ""
            }`}
            disabled={!isAdmin || isOwnAccount}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>

        <div>
          <div className="text-gray-400 text-sm mb-4">Permissions</div>
          {availablePermissions.length > 0 ? (
            <div className="space-y-3">
              {availablePermissions.map((permissionKey) => (
                <div key={permissionKey} className="flex items-center justify-between py-2 px-3 bg-gray-800 rounded-lg">
                  <div className="text-white">
                    {PermissionMapper[permissionKey] || permissionKey.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}
                  </div>
                  
                  <div className="flex items-center">
                    <span className="mr-3 text-sm text-gray-300">
                      {user.permissions?.[permissionKey] ? "Enabled" : "Disabled"}
                    </span>
                    <button
                      type="button"
                      onClick={() => isAdmin && handlePermissionChange(permissionKey, !user.permissions?.[permissionKey])}
                      disabled={!isAdmin}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                        user.permissions?.[permissionKey] 
                          ? 'bg-blue-600' 
                          : 'bg-gray-600'
                      } ${
                        !isAdmin ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                          user.permissions?.[permissionKey] 
                            ? 'translate-x-6' 
                            : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-sm">No permissions available for this user type</div>
          )}
        </div>

        <button
          onClick={handleUpdate}
          className={`mt-6 w-full py-3 rounded-lg text-white ${
            !hasChanges() || updating 
              ? "bg-gray-600 cursor-not-allowed" 
              : "bg-blue-600 hover:bg-blue-700"
          }`}
          disabled={!hasChanges() || updating}
        >
          {updating ? "Updating..." : "Update"}
        </button>

        <button
          onClick={handleResetPassword}
          className={`mt-4 w-full py-3 rounded-lg text-white ${
            !isAdmin 
              ? "bg-gray-600 cursor-not-allowed opacity-50" 
              : "bg-blue-600 hover:bg-blue-700"
          }`}
          disabled={!isAdmin}
        >
          Reset Password
        </button>
      </div>
    </div>
  );
};

export default AccountInformation;